import { motion } from "framer-motion";
import { Phone, MapPin, Check, Star, Users, Award, Shield, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IMAGES } from "@/assets/images";
import { SHOWROOM_LOCATIONS, CONTACT_INFO, PROMOTION_DETAILS, COMPANY_INFO } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Home() {

  const benefits = [
    {
      icon: Award,
      title: "Oltre 50 Anni di Esperienza",
      description: "Dal 1976, leader nell'arredamento in Piemonte e Emilia-Romagna",
    },
    {
      icon: Users,
      title: "Consulenza Personalizzata",
      description: "Team di esperti dedicati per aiutarti a creare la cameretta perfetta",
    },
    {
      icon: Shield,
      title: "Qualità Garantita",
      description: "Prodotti certificati e sicuri per i tuoi bambini",
    },
    {
      icon: Star,
      title: "Servizio Completo",
      description: "Consegna, montaggio e pagamenti flessibili inclusi",
    },
  ];

  const galleryImages = [
    { src: IMAGES.CAMERETTA_JOB_1, alt: "Cameretta moderna bianca e azzurra" },
    { src: IMAGES.CAMERETTA_JOB_5, alt: "Cameretta salvaspazio con letto a castello" },
    { src: IMAGES.CAMERETTA_JOB_10, alt: "Cameretta colorata con scrivania integrata" },
    { src: IMAGES.CAMERETTA_JOB_15, alt: "Cameretta componibile rosa e bianca" },
    { src: IMAGES.CAMERETTA_JOB_20, alt: "Cameretta con armadio angolare" },
    { src: IMAGES.CAMERETTA_JOB_25, alt: "Cameretta verde e legno naturale" },
    { src: IMAGES.CAMERETTA_JOB_3, alt: "Cameretta doppia con due letti" },
    { src: IMAGES.CAMERETTA_JOB_8, alt: "Cameretta moderna grigia e gialla" },
    { src: IMAGES.CAMERETTA_JOB_12, alt: "Cameretta con ponte e scrivania" },
    { src: IMAGES.CAMERETTA_JOB_18, alt: "Cameretta blu e bianca marinara" },
    { src: IMAGES.CAMERETTA_JOB_22, alt: "Cameretta con letto singolo e armadio" },
    { src: IMAGES.CAMERETTA_JOB_28, alt: "Cameretta arancione e legno" },
  ];

  return (
    <div className="min-h-screen">
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CAMERETTA_JOB_1}
            alt="Cameretta Job Giessegi"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <motion.div
          className="container mx-auto px-4 relative z-10 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
        >
          <motion.div
            className="inline-block mb-6 px-6 py-3 bg-primary/10 border border-primary/20 rounded-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...springPresets.bouncy, delay: 0.2 }}
          >
            <p className="text-primary font-mono font-semibold text-sm md:text-base">
              🎉 PROMOZIONE ESCLUSIVA 2026
            </p>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
          >
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              {PROMOTION_DETAILS.discount} di Sconto
            </span>
            <br />
            <span className="text-foreground">su {PROMOTION_DETAILS.category} {PROMOTION_DETAILS.brand}</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springPresets.gentle, delay: 0.4 }}
          >
            Crea lo spazio perfetto per i tuoi bambini con le camerette Giessegi.
            Qualità, sicurezza e design italiano garantiti dal leader delle camerette.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.5 }}
          >
            <Button
              size="lg"
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
              asChild
            >
              <a href="https://mobilandiaemilia.it/appuntamento-negozio/" target="_blank" rel="noopener noreferrer">
                Prenota un Appuntamento
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 border-2"
              onClick={() => document.getElementById('camerette')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Scopri le Camerette
            </Button>
          </motion.div>

          <motion.div
            className="mt-12 flex items-center justify-center gap-2 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springPresets.gentle, delay: 0.6 }}
          >
            <Clock className="w-4 h-4" />
            <span>Offerta valida fino al {new Date(PROMOTION_DETAILS.validUntil).toLocaleDateString('it-IT')}</span>
          </motion.div>
        </motion.div>
      </section>

      <section id="promozione" className="py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Perché Questa Promozione?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Vogliamo rendere accessibile a tutte le famiglie la qualità e il design delle camerette Giessegi
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {[
              {
                title: "50% di Risparmio Reale",
                description: "Sconto applicato su tutte le camerette Giessegi selezionate",
                highlight: "Risparmio garantito",
              },
              {
                title: "Pagamenti Flessibili",
                description: "I nostri arredatori sapranno consigliarti un metodo di pagamento flessibile e personalizzato",
                highlight: "Soluzioni su misura",
              },
              {
                title: "Trasporto e Montaggio",
                description: "Servizio operato da professionisti qualificati. Non devi pensare a nulla",
                highlight: "Servizio professionale",
              },
            ].map((item, index) => (
              <motion.div key={index} variants={staggerItem}>
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105 border-2">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <Check className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground mb-4">{item.description}</p>
                    <p className="text-primary font-mono font-semibold">{item.highlight}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="camerette" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <h2 className="text-4xl md:text-5xl font-bold">Camerette</h2>
              <img 
                src={IMAGES.LOGO_GIESSEGI} 
                alt="Logo Giessegi" 
                className="h-12 md:h-16 object-contain"
              />
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Soluzioni moderne, componibili e salvaspazio Giessegi che crescono con i tuoi bambini.
              Il leader italiano delle camerette per ragazzi.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-12"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {[
              {
                title: "Design Moderno",
                description: "Linee pulite e colori vivaci per stimolare la creatività",
              },
              {
                title: "Componibili",
                description: "Moduli flessibili che si adattano alle esigenze di crescita",
              },
              {
                title: "Salvaspazio",
                description: "Soluzioni intelligenti per ottimizzare ogni centimetro",
              },
            ].map((feature, index) => (
              <motion.div key={index} variants={staggerItem} className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <div className="flex items-center justify-center gap-6 mb-8">
              <img 
                src={IMAGES.LOGO_GIESSEGI} 
                alt="Logo Giessegi" 
                className="h-16 md:h-20 object-contain"
              />
              <div className="text-left">
                <h2 className="text-3xl md:text-4xl font-bold mb-2">Giessegi</h2>
                <p className="text-lg text-muted-foreground">Il leader italiano delle camerette</p>
              </div>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Da oltre 40 anni Giessegi è sinonimo di qualità, innovazione e design nel mondo delle camerette per bambini e ragazzi. 
              Ogni cameretta è progettata per crescere insieme ai tuoi figli, garantendo sicurezza, funzionalità e stile italiano.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Qualità Certificata</h3>
                <p className="text-muted-foreground">Materiali sicuri e certificati per la salute dei bambini</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Design Premiato</h3>
                <p className="text-muted-foreground">Soluzioni innovative riconosciute a livello internazionale</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Garanzia Italiana</h3>
                <p className="text-muted-foreground">Produzione 100% italiana con garanzia di lunga durata</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="perche-sceglierci" className="py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Perché Scegliere Mobilandia</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {COMPANY_INFO.experience} anni di esperienza al servizio delle famiglie italiane
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section id="showroom" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">I Nostri Showroom</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Vieni a trovarci in uno dei nostri 4 showroom in Emilia-Romagna
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {SHOWROOM_LOCATIONS.map((location, index) => (
              <motion.div key={location.id} variants={staggerItem}>
                <Card className="hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-semibold mb-4 text-center">{location.city}</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-muted-foreground">{location.address}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                        <a href={`https://wa.me/${location.phone}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                          {location.phone}
                        </a>
                      </div>

                    </div>
                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={() => window.open(location.mapUrl, '_blank')}
                    >
                      Maggiori Informazioni
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="contatti" className="py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Contattaci Subito</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Scegli il modo più comodo per te: WhatsApp per una risposta immediata o prenota un appuntamento con un nostro arredatore
            </p>
          </motion.div>

          <motion.div
            className="max-w-md mx-auto space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <Button
              size="lg"
              className="w-full text-lg py-8 bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
              asChild
            >
              <a href={`https://wa.me/${CONTACT_INFO.phone}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                <Phone className="w-6 h-6" />
                Scrivici su WhatsApp
              </a>
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="w-full text-lg py-8 border-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
              asChild
            >
              <a href="https://mobilandiaemilia.it/appuntamento-negozio/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                <Calendar className="w-6 h-6" />
                Prenota un Appuntamento con un Arredatore
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
